import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from './sidebar';
import OrderDetailsModal from './modal_windows/OrderDetailsModal';

import {
  StatCard, RefreshButton, SearchField, FilterPill,
  StatusBadge, TableCard, LoadingState, EmptyState, Pagination,
} from '../../ui/CrmPage';

const PAGE_SIZE = 15;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    timeZone: 'Europe/Moscow',
  });
};

const ArchiveOrders = () => {
  const [orders, setOrders] = useState([]);
  const [orderDetails, setOrderDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Все');
  const [regionFilter, setRegionFilter] = useState('Все');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}v1/archive_view`)
      .then(response => response.json())
      .then(data => {
        if (Array.isArray(data)) {
          setOrders(data);
          data.forEach(order => fetchOrderDetails(order.id_orders));
        }
      })
      .catch(error => console.error('Ошибка:', error))
      .finally(() => setLoading(false));
  }, [reloadKey]);

  const fetchOrderDetails = (orderId) => {
    fetch(`${process.env.REACT_APP_API_URL}v1/order_orderitems/${orderId}`)
      .then(response => response.json())
      .then(data => setOrderDetails(prev => ({ ...prev, [orderId]: data })))
      .catch(error => console.error('Ошибка:', error));
  };

  const handleOrderClick = (orderId) => {
    const order = orders.find(o => o.id_orders === orderId);
    const details = orderDetails[orderId];
    if (order) {
      const items = Array.isArray(details)
        ? details.filter(item => Number(item['Количество'] || item.quantity) > 0)
        : [];
      setSelectedOrderDetails({ ...order, id: order.id_orders, items });
      setIsModalOpen(true);
    }
  };

  const summary = useMemo(() => {
    const total = orders.length;
    const closed = orders.filter(o => /закрыт|доставлен/i.test(o.status_name || '')).length;
    const cancelled = orders.filter(o => /отмен/i.test(o.status_name || '')).length;
    const returned = orders.filter(o => /возврат|отклон/i.test(o.status_name || '')).length;
    return { total, closed, cancelled, returned };
  }, [orders]);

  const statusGroups = useMemo(() =>
    Array.from(new Set(orders.map(o => o.status_name).filter(Boolean))), [orders]);

  const regionGroups = useMemo(() =>
    Array.from(new Set(orders.map((o) => o.region_name).filter(Boolean))).sort(), [orders]);

  const filteredOrders = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return orders.filter(o => {
      if (statusFilter !== 'Все' && o.status_name !== statusFilter) return false;
      if (regionFilter !== 'Все' && (o.region_name || '') !== regionFilter) return false;
      if (!term) return true;
      return (
        (o.client_name || '').toLowerCase().includes(term) ||
        (o.delivery_address || '').toLowerCase().includes(term) ||
        String(o.order_number || '').includes(term)
      );
    });
  }, [orders, searchTerm, statusFilter, regionFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const paginatedOrders = filteredOrders.slice(pageStart, pageStart + PAGE_SIZE);

  const resetFilters = () => { setSearchTerm(''); setStatusFilter('Все'); setRegionFilter('Все'); setCurrentPage(1); };
  const hasFilters = searchTerm || statusFilter !== 'Все' || regionFilter !== 'Все';

  return (
    <div className="archive-orders-container crmpage">
      <div className="archive-orders-sidebar">
        <Sidebar />
      </div>

      <div className="archive-orders-basic-container crmpage__main">
        <header className="crmpage__head">
          <h1 className="crmpage__title">Архив заказов</h1>
        </header>

        <section className="crmpage__stats">
          <StatCard label="Всего"      value={summary.total}     accent="neutral" />
          <StatCard label="Закрыты"    value={summary.closed}    accent="success" />
          <StatCard label="Отменены"   value={summary.cancelled} accent="danger" />
          <StatCard label="Возвраты"   value={summary.returned}  accent="warning" />
          <RefreshButton onClick={() => setReloadKey(k => k + 1)} />
        </section>

        <section className="crmpage__filters">
          <SearchField
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Поиск: номер, клиент, адрес…"
          />
          <div className="crmpage__pills">
            <FilterPill active={statusFilter === 'Все'}
              onClick={() => { setStatusFilter('Все'); setCurrentPage(1); }}
              count={orders.length}>Все</FilterPill>
            {statusGroups.map(s => (
              <FilterPill key={s}
                active={statusFilter === s}
                onClick={() => { setStatusFilter(s); setCurrentPage(1); }}
                count={orders.filter(o => o.status_name === s).length}>{s}</FilterPill>
            ))}
          </div>
          {hasFilters && (
            <button type="button" className="crmpage__reset" onClick={resetFilters}>Сбросить</button>
          )}
        </section>

        <section className="crmpage__filters crmpage__filters--regions">
          <div className="crmpage__pills">
            <FilterPill
              active={regionFilter === 'Все'}
              onClick={() => { setRegionFilter('Все'); setCurrentPage(1); }}
              count={orders.length}
            >Все регионы</FilterPill>
            {regionGroups.map((r) => (
              <FilterPill
                key={r}
                active={regionFilter === r}
                onClick={() => { setRegionFilter(r); setCurrentPage(1); }}
                count={orders.filter((o) => (o.region_name || '') === r).length}
              >{r}</FilterPill>
            ))}
          </div>
        </section>

        <TableCard>
          {loading ? (
            <LoadingState text="Загружаем архив…" />
          ) : paginatedOrders.length === 0 ? (
            <EmptyState title="Архив пуст" text={hasFilters ? 'Сбросьте фильтры.' : 'Здесь будут завершённые и закрытые заказы.'} />
          ) : (
            <>
              <div className="crmpage__table-wrap">
                <table className="crmpage__table">
                  <colgroup>
                    <col style={{ width: 80 }} />
                    <col />
                    <col />
                    <col style={{ width: 160 }} />
                    <col style={{ width: 100 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Номер</th>
                      <th>Клиент</th>
                      <th className="crmpage__hide-880">Адрес доставки</th>
                      <th>Статус</th>
                      <th className="crmpage__hide-1000">Доставка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedOrders.map((order, idx) => (
                      <tr
                        key={idx}
                        className="crmpage__row"
                        onClick={() => handleOrderClick(order.id_orders)}
                      >
                        <td><span className="crmpage__num">№{order.order_number}</span></td>
                        <td>
                          <div className="crmpage__primary">{order.client_name || '—'}</div>
                          <div className="crmpage__sub">{order.region_name}</div>
                        </td>
                        <td className="crmpage__muted crmpage__hide-880">{order.delivery_address || '—'}</td>
                        <td><StatusBadge name={order.status_name} /></td>
                        <td className="crmpage__date crmpage__hide-1000">{formatDate(order.delivery_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="crmpage__cards">
                {paginatedOrders.map((o, idx) => (
                  <div key={idx} className="crmpage__card" onClick={() => handleOrderClick(o.id_orders)}>
                    <div className="crmpage__card-head">
                      <span className="crmpage__num">№{o.order_number}</span>
                      <StatusBadge name={o.status_name} />
                    </div>
                    <div className="crmpage__card-row">
                      <span className="crmpage__card-label">Клиент</span>
                      <span className="crmpage__card-val">{o.client_name || '—'}</span>
                    </div>
                    <div className="crmpage__card-row">
                      <span className="crmpage__card-label">Адрес</span>
                      <span className="crmpage__card-val">{o.delivery_address || '—'}</span>
                    </div>
                    <div className="crmpage__card-row">
                      <span className="crmpage__card-label">Доставка</span>
                      <span className="crmpage__card-val">{formatDate(o.delivery_date)}</span>
                    </div>
                  </div>
                ))}
              </div>

              <Pagination current={currentPage} total={totalPages} onChange={setCurrentPage} />
            </>
          )}
        </TableCard>
      </div>

      {isModalOpen && selectedOrderDetails && (
        <OrderDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          order={selectedOrderDetails}
        />
      )}
    </div>
  );
};

export default ArchiveOrders;
